import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useGetFactDetails } from '@/api/queries/useGetFactDetails';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { RelevanceOptions } from '@/constants/filterOptions';
import { Chip } from '@/new/components/chip';
import { FactDocViewer } from '@/new/components/doc-viewer/fact-doc-viewer';
import { CommentSection } from '@/new/components/fact/comments-section';
import { SourceConfidenceWarning } from '@/new/components/fact/source-confidence-warning';
import { Icon } from '@/new/components/icon';
import { ProcessingChip, ProcessingState } from '@/new/components/processing-chip';
import { DocumentTabs } from '@/new/components/reference-content/document-tabs';
import { DrawerErrorState } from '@/new/components/reference-content/drawer-error-state';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { TooltipIcon } from '@/new/components/tooltip-icon';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { cn } from '@/new/lib/utils';

interface FactDocumentDrawerContentProps {
  factId: string;
}

export function FactDocumentDrawerContent({ factId }: FactDocumentDrawerContentProps) {
  const { matterId, subsetId } = useParams();
  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const requestSubsetId = subsetId ?? matter?.allDocumentsCaseId ?? undefined;

  const { data: fact, isLoading, error } = useGetFactDetails(matterId, requestSubsetId, factId);
  const [isFactInfoExpanded, setIsFactInfoExpanded] = useState(false);
  const [isInfoPanelCollapsed, setIsInfoPanelCollapsed] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sourcesScrollRef = useRef<HTMLDivElement>(null);
  const [isSourcesScrolledToBottom, setIsSourcesScrolledToBottom] = useState(false);

  const selectedLocation = useMemo(
    () => fact?.locations.find((l) => l.docId === selectedDocId),
    [fact?.locations, selectedDocId],
  );

  const sortedSources = useMemo(
    () => selectedLocation?.sources?.toSorted((a, b) => parseInt(a.pages) - parseInt(b.pages) || 0),
    [selectedLocation?.sources],
  );

  const docCount = fact?.locations.length ?? 0;
  const docIndex = useMemo(
    () => fact?.locations.findIndex((l) => l.docId === selectedDocId) ?? -1,
    [fact?.locations, selectedDocId],
  );

  const goToDoc = (offset: number) => {
    if (!fact?.locations || docIndex < 0) return;
    const next = fact.locations[docIndex + offset];
    if (next) setSelectedDocId(next.docId);
  };

  // Hide the bottom fade when the sources are scrolled to the bottom, or when the
  // content is too short to scroll at all.
  const updateSourcesScrollState = useCallback(() => {
    const target = sourcesScrollRef.current;
    if (target) {
      setIsSourcesScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
    }
  }, []);

  useEffect(() => {
    const target = sourcesScrollRef.current;
    if (!target) return;

    updateSourcesScrollState();

    const observer = new ResizeObserver(updateSourcesScrollState);
    observer.observe(target);
    return () => observer.disconnect();
  }, [updateSourcesScrollState, sortedSources]);

  // When a fact is loaded, auto-select a document (preserve current selection if valid)
  useEffect(() => {
    if (fact && fact.locations && fact.locations.length > 0) {
      setSelectedDocId((prev) => {
        if (prev && fact.locations.some((l) => l.docId === prev)) {
          return prev;
        }
        return fact.locations[0].docId;
      });
    }
  }, [fact]);

  // Reset state when factId changes
  useEffect(() => {
    setIsFactInfoExpanded(false);
    setIsInfoPanelCollapsed(false);
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [factId]);

  return (
    <div className="flex h-full flex-col">
      {error ? (
        <DrawerErrorState label="fact" />
      ) : (
        <>
          {isLoading && (
            <div className="my-20 flex h-full w-full items-center justify-center">
              <Icon className="lucide--loader animate-spin ease-in-out" />
            </div>
          )}
          {fact && (
            <>
              {/* Document Viewer Section */}
              <div className="-mb-8 flex-1 overflow-hidden">
                <FactDocViewer
                  docId={selectedDocId ?? ''}
                  factId={factId}
                  hideFeatureButtons={false}
                  hideCloseButton={true}
                  onViewerClick={() => setIsInfoPanelCollapsed(true)}
                />
              </div>

              <div className="z-10 max-h-9/10 overflow-hidden" onMouseEnter={() => setIsInfoPanelCollapsed(false)}>
                <DocumentTabs
                  tabs={fact.locations.map((l) => ({ id: l.docId, label: l.doc.file.name }))}
                  selectedId={selectedDocId}
                  onSelect={setSelectedDocId}
                />

                <motion.div
                  className="relative z-20 -mt-px overflow-hidden border-t bg-white text-xs shadow-[0_0_28px_rgba(0,0,0,0.2)]"
                  animate={{ height: isInfoPanelCollapsed ? '16px' : 'auto' }}
                  transition={{ duration: 0.2 }}
                >
                  <div
                    ref={scrollContainerRef}
                    className={cn(
                      isFactInfoExpanded ? 'overflow-y-auto' : 'overflow-hidden',
                      'max-h-[calc(90vh-10rem)] px-5',
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="my-2 mt-3 flex w-full items-center justify-between gap-1.5">
                        {docCount > 1 ? (
                          <div className="-ml-1.5 flex items-center gap-0.5">
                            <Button
                              size="iconSm"
                              variant="ghost"
                              disabled={docIndex <= 0}
                              onClick={() => goToDoc(-1)}
                              aria-label="Previous source document"
                            >
                              <Icon className="lucide--arrow-left" />
                            </Button>
                            <p className="font-medium tabular-nums">
                              Source Document {docIndex + 1}/{docCount}
                            </p>
                            <Button
                              size="iconSm"
                              variant="ghost"
                              disabled={docIndex >= docCount - 1}
                              onClick={() => goToDoc(1)}
                              aria-label="Next source document"
                            >
                              <Icon className="lucide--arrow-right" />
                            </Button>
                          </div>
                        ) : (
                          <p className="font-medium">Source Text</p>
                        )}

                        <div className="mt-0.5 flex items-center gap-2">
                          <Button
                            variant={'ghost'}
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                selectedLocation?.sources?.map((source) => source.text).join('\n\n') ?? '',
                              );
                              toast.success('Source text copied to clipboard');
                            }}
                            className="px-1.5 py-0.75"
                          >
                            <Icon className="lucide--copy" />
                            <span className="text-[11px]">Copy Source Text</span>
                          </Button>

                          <Chip className={CHIP_COLOURS.type}>{selectedLocation?.doc?.type}</Chip>
                        </div>
                      </div>
                    </div>

                    <div
                      ref={sourcesScrollRef}
                      onScroll={updateSourcesScrollState}
                      className="flex max-h-[30vh] flex-col gap-1.5 overflow-y-auto mask-b-from-70% mask-b-to-105% text-gray-600 transition-[mask-size]"
                      style={{ maskSize: isSourcesScrolledToBottom ? '100% 130%' : '100% 100%' }}
                    >
                      {sortedSources?.map((s, i) => (
                        <div key={i} className="text-xs">
                          <div className="mb-1 flex items-center gap-2">
                            <span className="font-medium text-gray-700">
                              [Page{s.pages.includes(',') ? 's' : ''} {s.pages}]
                            </span>
                            <SourceConfidenceWarning sourceConfidence={s.sourceConfidence} />
                          </div>
                          <p className="italic">{s.text}</p>
                        </div>
                      ))}
                    </div>

                    <hr className="-mx-5 my-3.5" />

                    <div className="space-y-1.5">
                      <div className={cn('flex items-center gap-1.5 font-semibold break-all text-gray-700')}>
                        <p>Fact</p>
                        <p className="text-[10px] text-gray-400/80">•</p>
                        <span className="-mb-0.25 line-clamp-1">{fact.factDateText ?? '(Date Unknown)'}</span>
                      </div>

                      <p className={`text-gray-700`}>{fact.description}</p>
                    </div>

                    <div className="pt-1.5 pb-2">
                      <button
                        className="flex w-full justify-center p-1 text-gray-400 duration-150 hover:text-gray-900"
                        onClick={() => {
                          if (isFactInfoExpanded) {
                            scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                          setIsFactInfoExpanded(!isFactInfoExpanded);
                        }}
                      >
                        <Icon
                          className={cn(
                            isFactInfoExpanded ? 'lucide--chevron-down' : 'lucide--more-horizontal',
                            'text-base duration-300',
                          )}
                        />
                      </button>
                    </div>

                    <motion.div
                      animate={{ height: isFactInfoExpanded ? 'auto' : 0 }}
                      transition={{ duration: 0.2 }}
                      initial={false}
                    >
                      <div className="pb-5">
                        <div
                          className={cn(
                            'relative -mx-1 flex flex-col gap-3 rounded-lg border bg-gray-50 px-4 py-3 text-xs',
                            !subset?.isAllDocuments && 'pt-9',
                          )}
                        >
                          {/* subset indicator */}
                          {!subset?.isAllDocuments && (
                            <div className="absolute -top-[1px] left-3 flex items-center gap-1.5 rounded-b-lg border bg-white px-2 py-1 text-xs font-medium text-gray-700">
                              <Icon className="lucide--group" />
                              <span>{subset?.name ?? ''}</span>
                            </div>
                          )}

                          {/* significance */}
                          <div className="flex grow flex-col gap-2">
                            <p className="flex items-center gap-2 font-medium">
                              <span>{subset?.isAllDocuments ? '' : 'Subset'} Significance</span>

                              <TooltipIcon
                                side="right"
                                tooltipContent={`Short analysis of this fact's significance as it pertains to this ${subset?.isAllDocuments ? `matter's` : `subset's`} issues`}
                              />
                            </p>

                            {fact.significance ? (
                              <p className="text-gray-700">{fact.significance}</p>
                            ) : (
                              <div className="flex flex-col gap-1.5">
                                <Skeleton className="h-4 w-9/10 bg-gray-200" />
                                <Skeleton className="h-4 w-2/10 bg-gray-200" />
                              </div>
                            )}
                          </div>

                          <div className="flex flex-col gap-2">
                            <p className="flex items-center gap-2 font-medium whitespace-nowrap">
                              {subset?.isAllDocuments ? '' : 'Subset'} Relevance
                              <TooltipIcon
                                side="right"
                                tooltipContent={`Is this fact relevant to the ${subset?.isAllDocuments ? 'matter' : 'subset'} issues?`}
                              />
                            </p>

                            {fact.relevance ? (
                              <p>{RelevanceOptions[fact.relevance].label}</p>
                            ) : (
                              <ProcessingChip
                                processingState={ProcessingState.Processing}
                                processingText="Processing Relevance"
                              />
                            )}
                          </div>
                        </div>

                        <div className="mx-1 mt-4">
                          <CommentSection fact={fact} />
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
